import { apiService } from '../../../core/api/BaseApiService';
import type { UserRoleWithPermissions } from '../../../core/types/permissions.types';

type EmptyObject = Record<string, never>;

// ── API response shapes ───────────────────────────────────────────────────────

export interface RequestLoginOtpApiResponse {
  isSuccess: boolean;
  statusCode: number;
  response: {
    user_id?: string;
    userId?: string;
    id?: string;
    _id?: string;
    user?: { id?: string; _id?: string; user_id?: string };
  } | EmptyObject;
  error: null | string;
  message: string;
}

export interface VerifyOtpApiResponse {
  isSuccess: boolean;
  statusCode: number;
  response: {
    user: {
      id:         string;
      name:       string;
      email:      string;
      username:   string;
      is_authenticated?: boolean;
      is_active:  boolean;
      created_at: string;
      updated_at: string;
      role: UserRoleWithPermissions;
      user_image: string | null;
    };
    access_token:  string;
    refresh_token: string;
  };
  error: null | string;
  message: string;
}

export interface ResendOtpApiResponse {
  isSuccess: boolean;
  statusCode: number;
  response: EmptyObject;
  error: null | string;
  message: string;
}

export interface RefreshTokenApiResponse {
  isSuccess: boolean;
  statusCode: number;
  response: {
    access_token:  string;
    refresh_token: string;
  };
  error:   null | string;
  message: string;
}

export interface LogoutApiResponse {
  isSuccess: boolean;
  statusCode: number;
  response:  object;
  error:     null | string;
  message:   string;
}

// ── Service ───────────────────────────────────────────────────────────────────

/**
 * All methods delegate to the shared apiService singleton so every feature
 * module shares the same Axios instance (connection pool + interceptors).
 */
export const authApiService = {
  /**
   * POST /login
   * Body: { username, password }
   * The "username" field accepts both an email address and a plain username.
   */
  requestLoginOtp(credentials: {
    username: string;
    password: string;
  }): Promise<RequestLoginOtpApiResponse> {
    return apiService.post<RequestLoginOtpApiResponse>('/login', credentials);
  },

  /**
   * POST /verify-otp
   * Body: { user_id, otp }
   */
  verifyOtp(payload: {
    user_id: string;
    otp: string;
  }): Promise<VerifyOtpApiResponse> {
    return apiService.post<VerifyOtpApiResponse>('/verify-otp', payload);
  },

  /**
   * POST /resend-otp
   * Body: { user_id }
   */
  resendOtp(payload: {
    user_id: string;
  }): Promise<ResendOtpApiResponse> {
    return apiService.post<ResendOtpApiResponse>('/resend-otp', payload);
  },

  /**
   * POST /refresh
   * Body: { refresh_token }
   */
  refreshToken(refreshToken: string): Promise<RefreshTokenApiResponse> {
    return apiService.post<RefreshTokenApiResponse>('/refresh', {
      refresh_token: refreshToken,
    });
  },

  /**
   * POST /logout
   * Bearer token attached automatically by the request interceptor.
   */
  logout(): Promise<LogoutApiResponse> {
    return apiService.post<LogoutApiResponse>('/logout');
  },
};
